import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { betCodeAPI, competitionAPI, h2hAPI, rolloverAPI, userAPI } from '../api/endpoints';
import { formatXPUNT, formatTimeAgo, getStatusColor } from '../utils/helpers';
import { 
  FaWallet, FaTicketAlt, FaTrophy, FaHandshake, 
  FaChartLine, FaUsers, FaBell, FaCog, FaUser,
  FaArrowRight, FaFire, FaStar, FaClock
} from 'react-icons/fa';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const { balance, totalWinnings } = useWallet();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    betCodes: 0,
    competitions: 0,
    h2hGames: 0,
    rolloverGames: 0,
    winRate: 0,
    totalPoints: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [joinedCompetitions, setJoinedCompetitions] = useState([]);
  const [myBetCodes, setMyBetCodes] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch user stats
      const [betCodesRes, compsRes, h2hRes, rolloverRes, joinedComps, userPredictions] = await Promise.all([
        betCodeAPI.getList({ author: user?.id, per_page: 1 }),
        competitionAPI.getList({ per_page: 1 }),
        h2hAPI.getList({ per_page: 1 }),
        rolloverAPI.getList({ per_page: 1 }),
        userAPI.getJoinedCompetitions(),
        userAPI.getPredictions(),
      ]);

      setStats({
        betCodes: betCodesRes.data.total || 0,
        competitions: compsRes.data.total || 0,
        h2hGames: h2hRes.data.total || 0,
        rolloverGames: rolloverRes.data.total || 0,
        winRate: user?.win_rate || 0,
        totalPoints: user?.total_points || 0,
      });

      setJoinedCompetitions(joinedComps.data || []);
      
      // Get user's recent bet codes
      const myBetsRes = await betCodeAPI.getList({ author: user?.id, per_page: 5 });
      setMyBetCodes(myBetsRes.data.data || []);

      // Build recent activity
      const activity = [];
      if (userPredictions.data && userPredictions.data.length > 0) {
        activity.push({
          type: 'prediction',
          icon: '🎯',
          text: `Made a prediction in ${userPredictions.data[0]?.competition_id || 'a competition'}`,
          time: userPredictions.data[0]?.created_at,
        });
      }
      if (myBetsRes.data.data && myBetsRes.data.data.length > 0) {
        activity.push({
          type: 'bet_code',
          icon: '📝',
          text: `Created bet code: ${myBetsRes.data.data[0]?.title}`,
          time: myBetsRes.data.data[0]?.created_at,
        });
      }
      setRecentActivity(activity.slice(0, 10));

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Welcome Section */}
      <div className="dashboard-welcome">
        <div className="welcome-text">
          <h1>Welcome back, {user?.display_name || 'Punter'}! 👋</h1>
          <p>Here's what's happening with your betting activity</p>
        </div>
        <div className="welcome-actions">
          <Link to="/bet-codes/create" className="btn btn-primary">
            <FaFire /> Create Bet Code
          </Link>
          <Link to="/wallet" className="btn btn-secondary">
            <FaWallet /> Wallet
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card gold">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <span className="stat-value">{formatXPUNT(balance)}</span>
            <span className="stat-label">Wallet Balance</span>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon">🏆</div>
          <div className="stat-info">
            <span className="stat-value">{formatXPUNT(totalWinnings)}</span>
            <span className="stat-label">Total Winnings</span>
          </div>
        </div>
        <div className="stat-card blue">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <span className="stat-value">{stats.winRate}%</span>
            <span className="stat-label">Win Rate</span>
          </div>
        </div>
        <div className="stat-card purple">
          <div className="stat-icon">⭐</div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalPoints}</span>
            <span className="stat-label">Total Points</span>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div className="quick-stat">
          <FaTicketAlt className="qs-icon" />
          <span className="qs-value">{stats.betCodes}</span>
          <span className="qs-label">Bet Codes</span>
        </div>
        <div className="quick-stat">
          <FaTrophy className="qs-icon" />
          <span className="qs-value">{stats.competitions}</span>
          <span className="qs-label">Competitions</span>
        </div>
        <div className="quick-stat">
          <FaHandshake className="qs-icon" />
          <span className="qs-value">{stats.h2hGames}</span>
          <span className="qs-label">H2H Games</span>
        </div>
        <div className="quick-stat">
          <FaChartLine className="qs-icon" />
          <span className="qs-value">{stats.rolloverGames}</span>
          <span className="qs-label">Rollovers</span>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="dashboard-grid">
        {/* Recent Activity */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3><FaBell /> Recent Activity</h3>
            <Link to="/activity" className="view-all">View All</Link>
          </div>
          {recentActivity.length === 0 ? (
            <div className="empty-state-small">
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="activity-list">
              {recentActivity.map((item, index) => (
                <div key={index} className="activity-item">
                  <span className="activity-icon">{item.icon}</span>
                  <div className="activity-content">
                    <p>{item.text}</p>
                    <span className="activity-time">{formatTimeAgo(item.time)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* My Bet Codes */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3><FaTicketAlt /> My Bet Codes</h3>
            <Link to="/bet-codes?author=me" className="view-all">View All</Link>
          </div>
          {myBetCodes.length === 0 ? (
            <div className="empty-state-small">
              <p>You haven't created any bet codes yet</p>
              <Link to="/bet-codes/create" className="btn btn-primary btn-sm">
                Create One
              </Link>
            </div>
          ) : (
            <div className="bet-code-list">
              {myBetCodes.map((bet) => (
                <Link to={`/bet-codes/${bet.id}`} key={bet.id} className="bet-code-item">
                  <div className="bet-code-info">
                    <span className="bet-code-title-sm">{bet.title}</span>
                    <span className="bet-code-code-sm">{bet.code}</span>
                  </div>
                  <span 
                    className="bet-code-status"
                    style={{ color: getStatusColor(bet.status) }}
                  >
                    {bet.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Joined Competitions */}
      {joinedCompetitions.length > 0 && (
        <div className="dashboard-card full">
          <div className="card-header">
            <h3><FaTrophy /> Joined Competitions</h3>
            <Link to="/competitions" className="view-all">View All</Link>
          </div>
          <div className="competition-grid">
            {joinedCompetitions.map((comp) => (
              <Link to={`/competitions/${comp.id}`} key={comp.id} className="competition-item">
                <h4>{comp.title}</h4>
                <div className="competition-meta">
                  <span>{comp.players_count || 0} players</span>
                  <span className={`status-badge ${comp.status}`}>
                    {comp.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="quick-links">
        <Link to="/profile" className="quick-link">
          <FaUser /> Profile
        </Link>
        <Link to="/settings" className="quick-link">
          <FaCog /> Settings
        </Link>
        <Link to="/explore" className="quick-link">
          <FaUsers /> Explore Creators
        </Link>
        <Link to="/leaderboard" className="quick-link">
          <FaChartLine /> Leaderboard
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;